import React, {
  FC,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  RiFileList3Line,
  RiAlertLine,
  RiProhibited2Line,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiRestartLine,
  RiDeleteBack2Line,
  RiFileInfoLine,
  RiScales3Line,
  RiCheckboxLine,
  RiBold,
  RiEyeLine,
  RiFontSize,
  RiFileListLine,
  RiAuctionLine,
  RiPieChartLine,
  RiFilter3Fill,
} from '@remixicon/react';
import styles from './ContentRuleCenter.module.css';
import QualityCard from '../../atoms/QualityCard';
import CapsuleDropdown from '../../molecules/CapsuleDropdown';
import Accordion from '../../atoms/Accordion';
import Typography from '../../atoms/Typography';
import Tag, { TagVariant } from '../../atoms/Tag';
import Button from '../../atoms/Button';
import CustomLoader from '../../molecules/CustomLoader';
import { SeverityCode } from '../../../types/ContentRules';
import { CannedParams } from '../../../types/Content';
import { GroupByConfig, GroupTooltipConfig } from '../../../types/SelectInput';
import { translate } from '../../../localization';

// Define Option and BrandCategory types based on your data structure
interface Option {
  name: string;
  label: string;
  category?: string;
}

enum BrandCategory {
  ONBOARDED = 'ONBOARDED',
  NON_ONBOARDED = 'NON_ONBOARDED',
}

/** Maximum number of tags to display before showing "Show More" */
const MAX_VISIBLE_TAGS = 6;
/** Maximum description length before truncation */
const MAX_DESCRIPTION_LENGTH = 150;
/** Minimum difference to show "Show More" - avoid showing for trivial differences */
const MIN_TRUNCATION_DIFFERENCE = 40;

/**
 * Stable empty array reference used as a default for array props.
 * A fresh `[]` in a default parameter is re-created on every render, which would make it a
 * new dependency on every run of the filters-initialization effect below, re-triggering that
 * effect (and its setState) forever whenever the corresponding prop is omitted.
 */
const EMPTY_ARRAY: CannedParams[] = [];
const EMPTY_STRING_ARRAY: string[] = [];

/**
 * Icon mapping for category accordion headers
 * Maps category names (case-insensitive) to their respective icons
 */
const getCategoryIcon = (categoryId: string): React.ReactElement | null => {
  if (categoryId === 'audience-fit') {
    return <RiPieChartLine size='18' color='#0000C9' />;
  }
  if (categoryId === 'legal') {
    return <RiAuctionLine size='18' color='#0000C9' />;
  }
  if (categoryId === 'content') {
    return <RiFileListLine size='18' color='#0000C9' />;
  }
  if (categoryId === 'compliance') {
    return <RiFontSize size='18' color='#0000C9' />;
  }
  if (categoryId === 'mlr') {
    return <RiScales3Line size='18' color='#0000C9' />;
  }
  if (categoryId === 'content_quality') {
    return <RiCheckboxLine size='18' color='#0000C9' />;
  }

  if (categoryId === 'brand_2.0_verbal_identity') {
    return <RiBold size='18' color='#0000C9' />;
  }

  if (categoryId === 'branding') {
    return <RiEyeLine size='18' color='#0000C9' />;
  }

  // Default fallback - no icon
  return null;
};

export interface RuleItem {
  id: string;
  name: string;
  description: string;
  tags: string[];
  severity: SeverityCode;
  lastUpdated: string;
}

export interface RuleCategory {
  id: string;
  name: string;
  icon?: React.ReactElement | null;
  mustComplyCount?: number;
  shouldComplyCount?: number;
  goodToHaveCount?: number;
  rules: RuleItem[];
  /** Loading state for this category's rules */
  isLoading?: boolean;
  /** Whether rules have been fetched for this category */
  isFetched?: boolean;
  /** Pagination info for this category's rules */
  pagination?: {
    page: number;
    pageSize: number;
    totalPages: number;
    total: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  /** Error for this category */
  error?: string | null;
}

export interface ContentRuleCenterProps {
  /**
   * Total count of all rules
   */
  allRulesCount: number;
  /**
   * Count of must comply rules
   */
  mustComplyCount: number;
  /**
   * Count of should comply rules
   */
  shouldComplyCount: number;
  /**
   * Count of good to have rules
   */
  goodToHaveCount: number;
  /**
   * Rule categories with their rules
   */
  categories: RuleCategory[];
  /**
   * Filter options for countries
   */
  countries?: CannedParams[];
  /**
   * Filter options for brands
   */
  brands?: CannedParams[];
  /**
   * Filter options for audiences
   */
  audiences?: CannedParams[];
  /**
   * Filter options for channels
   */
  channels?: CannedParams[];
  /**
   * Filter options for categories
   */
  categoryFilters?: string[];
  /**
   * Current selected filter values from store
   */
  selectedFilters?: ContentRuleFilters;
  /**
   * Callback when filters change
   */
  onFiltersChange?: (filters: ContentRuleFilters) => void;
  /**
   * Loading state (shows full page loader)
   */
  isLoading?: boolean;
  /**
   * Loading state for table content (category summaries)
   */
  isTableLoading?: boolean;
  /**
   * Whether filter options and quality cards are loaded
   */
  areFiltersLoaded?: boolean;
  /**
   * Error state
   */
  error?: string | null;
  /**
   * Callback for retry action on error
   */
  onRetry?: () => void;
  /**
   * Sort configuration
   */
  sortConfig?: {
    field: 'severity' | 'lastUpdated';
    direction: 'asc' | 'desc';
  };
  /**
   * Callback when sort changes
   */
  onSortChange?: (field: 'severity' | 'lastUpdated') => void;
  /**
   * Callback when an accordion is opened (for lazy loading rules)
   */
  onAccordionOpen?: (categoryId: string) => void;
  /**
   * Callback to load more rules for a category (for infinite scroll)
   */
  onLoadMoreRules?: (categoryId: string) => void;
}

export interface ContentRuleFilters {
  countries: string[];
  brands: string[];
  audiences: string[];
  channels: string[];
  categories: string[];
}

/**
 * ContentRuleCenter - A widget component for managing and viewing content rules
 * Displays rules organized by categories with filtering capabilities
 */
const ContentRuleCenter: FC<ContentRuleCenterProps> = ({
  allRulesCount,
  mustComplyCount,
  shouldComplyCount,
  goodToHaveCount,
  categories,
  countries = EMPTY_ARRAY,
  brands = EMPTY_ARRAY,
  audiences = EMPTY_ARRAY,
  channels = EMPTY_ARRAY,
  categoryFilters = EMPTY_STRING_ARRAY,
  selectedFilters: selectedFiltersProp,
  onFiltersChange,
  isLoading = false,
  isTableLoading = false,
  error = null,
  onRetry,
  // sortConfig,
  onSortChange,
  onAccordionOpen,
  onLoadMoreRules,
}) => {
  const [filters, setFilters] = useState<ContentRuleFilters>({
    countries: [],
    brands: [],
    audiences: [],
    channels: [],
    categories: [],
  });

  // Initialize filters from props (restore from store) or default to all options
  useEffect(() => {
    // If selectedFilters from store has values, use them
    if (
      selectedFiltersProp &&
      (selectedFiltersProp.countries.length > 0 ||
        selectedFiltersProp.brands.length > 0 ||
        selectedFiltersProp.audiences.length > 0 ||
        selectedFiltersProp.channels.length > 0 ||
        selectedFiltersProp.categories.length > 0)
    ) {
      setFilters(selectedFiltersProp);
    } else if (
      countries.length > 0 ||
      brands.length > 0 ||
      audiences.length > 0 ||
      channels.length > 0 ||
      categoryFilters.length > 0
    ) {
      // Default to all options selected
      const allFilters: ContentRuleFilters = {
        countries: countries.map(c => c.name) as string[],
        brands: brands.map(b => b.name) as string[],
        audiences: audiences.map(a => a.name) as string[],
        channels: channels.map(ch => ch.name) as string[],
        categories: categoryFilters,
      };
      setFilters(allFilters);
    }
  }, [
    selectedFiltersProp,
    countries,
    brands,
    audiences,
    channels,
    categoryFilters,
  ]);

  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set()
  );
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());

  // Refs for infinite scroll observers and scroll containers
  const observersRef = useRef<Map<string, IntersectionObserver>>(new Map());
  const scrollContainersRef = useRef<Map<string, HTMLDivElement>>(new Map());

  // Cleanup observers on unmount
  useEffect(() => {
    const observers = observersRef.current;
    return () => {
      observers.forEach(observer => observer.disconnect());
      observers.clear();
    };
  }, []);

  // Setup scroll container ref for infinite scroll
  const setScrollContainerRef = useCallback(
    (categoryId: string, element: HTMLDivElement | null) => {
      if (element) {
        scrollContainersRef.current.set(categoryId, element);
      } else {
        scrollContainersRef.current.delete(categoryId);
      }
    },
    []
  );

  // Setup infinite scroll observer for a category
  const setupInfiniteScroll = useCallback(
    (categoryId: string, element: HTMLDivElement | null) => {
      if (!element || !onLoadMoreRules) return;

      // Cleanup existing observer for this category
      const existingObserver = observersRef.current.get(categoryId);
      if (existingObserver) {
        existingObserver.disconnect();
        observersRef.current.delete(categoryId);
      }

      // Find the category data
      const category = categories.find(cat => cat.id === categoryId);
      if (!category?.pagination) return;

      // Only set up observer if there are more pages to load
      if (category.pagination.page >= category.pagination.totalPages) return;
      if (category.isLoading) return;

      // Get the scroll container for this category
      const scrollContainer = scrollContainersRef.current.get(categoryId);

      // Create intersection observer
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const cat = categories.find(c => c.id === categoryId);
              if (
                cat?.pagination &&
                cat.pagination.page < cat.pagination.totalPages &&
                !cat.isLoading
              ) {
                onLoadMoreRules(categoryId);
              }
            }
          });
        },
        {
          root: scrollContainer || null,
          rootMargin: '50px',
          threshold: 0.1,
        }
      );

      observer.observe(element);
      observersRef.current.set(categoryId, observer);
    },
    [categories, onLoadMoreRules]
  );

  const handleFilterChange = (filterType: keyof ContentRuleFilters) => {
    return (value: string | string[]) => {
      const newFilters = {
        ...filters,
        [filterType]: Array.isArray(value) ? value : [value],
      };
      setFilters(newFilters);
      onFiltersChange?.(newFilters);
    };
  };

  const handleResetFilters = () => {
    // Reset to all options selected (default state)
    const allFilters: ContentRuleFilters = {
      countries: countries.map(c => c.name) as string[],
      brands: brands.map(b => b.name) as string[],
      audiences: audiences.map(a => a.name) as string[],
      channels: channels.map(ch => ch.name) as string[],
      categories: categoryFilters,
    };
    setFilters(allFilters);
    onFiltersChange?.(allFilters);
  };

  // Check if any filter has been modified from the default "all selected" state
  const hasActiveFilters = useMemo(() => {
    const isCountriesFiltered =
      filters.countries.length > 0 &&
      filters.countries.length < countries.length;
    const isBrandsFiltered =
      filters.brands.length > 0 && filters.brands.length < brands.length;
    const isAudiencesFiltered =
      filters.audiences.length > 0 &&
      filters.audiences.length < audiences.length;
    const isChannelsFiltered =
      filters.channels.length > 0 && filters.channels.length < channels.length;
    const isCategoriesFiltered =
      filters.categories.length > 0 &&
      filters.categories.length < categoryFilters.length;

    return (
      isCountriesFiltered ||
      isBrandsFiltered ||
      isAudiencesFiltered ||
      isChannelsFiltered ||
      isCategoriesFiltered
    );
  }, [
    filters,
    countries.length,
    brands.length,
    audiences.length,
    channels.length,
    categoryFilters.length,
  ]);

  const visibleRulesCount = useMemo(() => {
    return categories.reduce((acc, cat) => acc + cat.rules.length, 0);
  }, [categories]);

  const brandTooltipConfig: GroupTooltipConfig = useMemo(
    () => ({
      [translate('brand_category_amplify')]: {
        text: translate('brand_category_amplify_tooltip'),
      },
      [translate('brand_category_explore')]: {
        text: translate('brand_category_explore_tooltip'),
      },
    }),
    []
  );

  const brandGroupingConfig: GroupByConfig[] = useMemo(() => {
    const onboardedBrands: Option[] = [];
    const nonOnboardedBrands: Option[] = [];

    (brands as unknown as Option[])?.forEach((brand: Option) => {
      if (brand.category === BrandCategory.ONBOARDED) {
        onboardedBrands.push(brand);
      } else if (brand.category === BrandCategory.NON_ONBOARDED) {
        nonOnboardedBrands.push(brand);
      }
    });

    // Sort brands alphabetically within each group
    onboardedBrands.sort(({ label: labelA }, { label: labelB }) =>
      labelA.localeCompare(labelB)
    );
    nonOnboardedBrands.sort(({ label: labelA }, { label: labelB }) =>
      labelA.localeCompare(labelB)
    );

    const brandGroupConfig: GroupByConfig[] = [];

    // Add Amplify group (ONBOARDED brands) first
    if (onboardedBrands.length > 0) {
      brandGroupConfig.push({
        title: translate('brand_category_amplify'),
        groupBy: onboardedBrands.map(el => el.name) as string[],
      });
    }

    // Add Explore group (NON_ONBOARDED brands) second
    if (nonOnboardedBrands.length > 0) {
      brandGroupConfig.push({
        title: translate('brand_category_explore'),
        groupBy: nonOnboardedBrands.map(el => el.name) as string[],
      });
    }

    return brandGroupConfig;
  }, [brands]);

  const toggleDescription = (ruleId: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  };

  const toggleTags = (ruleId: string) => {
    setExpandedTags(prev => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  };

  const getSeverityIcon = (severity: RuleItem['severity']) => {
    switch (severity) {
      case SeverityCode.BLOCKER:
        return (
          <RiProhibited2Line size={16} className={styles.severityIconMust} />
        );
      case SeverityCode.WARNING:
        return <RiAlertLine size={16} className={styles.severityIconShould} />;
      case SeverityCode.INFO:
        return <RiFileInfoLine size={16} className={styles.severityIconGood} />;
      default:
        return null;
    }
  };

  const getSeverityLabel = (severity: RuleItem['severity']) => {
    switch (severity) {
      case SeverityCode.BLOCKER:
        return translate('rules_center_must_apply');
      case SeverityCode.WARNING:
        return translate('rules_center_should_apply');
      case SeverityCode.INFO:
        return translate('rules_center_good_to_have_label');
      default:
        return '';
    }
  };

  const getCategorySeverityCounts = (category: RuleCategory) => {
    return {
      must: category.mustComplyCount || 0,
      should: category.shouldComplyCount || 0,
      good: category.goodToHaveCount || 0,
    };
  };

  const renderDescription = (rule: RuleItem) => {
    const isExpanded = expandedDescriptions.has(rule.id);
    // Only truncate if description is long enough AND the difference is meaningful
    const descriptionLength = rule.description?.length || 0;
    const shouldTruncate =
      rule.description &&
      descriptionLength > MAX_DESCRIPTION_LENGTH &&
      descriptionLength - MAX_DESCRIPTION_LENGTH >= MIN_TRUNCATION_DIFFERENCE;

    return (
      <>
        <Typography variant='text-sm' className={styles.ruleDescription}>
          {shouldTruncate && !isExpanded
            ? `${rule.description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
            : rule.description}
        </Typography>
        {shouldTruncate && (
          <button
            className={styles.showMoreLink}
            onClick={() => toggleDescription(rule.id)}
            type='button'
          >
            {isExpanded ? 'Show Less' : 'Show More'}
            {isExpanded ? (
              <RiArrowUpSLine size={12} />
            ) : (
              <RiArrowDownSLine size={12} />
            )}
          </button>
        )}
      </>
    );
  };

  const renderTags = (rule: RuleItem) => {
    const isExpanded = expandedTags.has(rule.id);
    const shouldTruncate = rule.tags && rule.tags.length > MAX_VISIBLE_TAGS;
    const visibleTags =
      isExpanded || !shouldTruncate
        ? rule.tags
        : rule.tags.slice(0, MAX_VISIBLE_TAGS);

    return (
      <>
        <div className={styles.tagsContainer}>
          {visibleTags.map(tag => (
            <Tag
              key={`${rule.id}-${tag}`}
              text={tag}
              variant={TagVariant.V2}
              icon={null}
            />
          ))}
        </div>
        {shouldTruncate && (
          <button
            className={styles.showMoreLink}
            onClick={() => toggleTags(rule.id)}
            type='button'
          >
            {isExpanded ? 'Show Less' : 'Show More'}
            {isExpanded ? (
              <RiArrowUpSLine size={12} />
            ) : (
              <RiArrowDownSLine size={12} />
            )}
          </button>
        )}
      </>
    );
  };

  // const renderSortIcon = (field: 'severity' | 'lastUpdated') => {
  //   if (!sortConfig || sortConfig.field !== field) {
  //     return (
  //       <span className={styles.sortIconInactive}>
  //         <RiArrowDownSLine size={16} />
  //       </span>
  //     );
  //   }
  //   return sortConfig.direction === 'asc' ? (
  //     <RiArrowUpSLine size={16} className={styles.sortIconActive} />
  //   ) : (
  //     <RiArrowDownSLine size={16} className={styles.sortIconActive} />
  //   );
  // };

  // Loading state - shows CustomLoader centered in modal
  if (isLoading) {
    return (
      <div className={styles.contentRuleCenter}>
        <div className={styles.loadingContainer}>
          <CustomLoader size={32} text={translate('rules_center_loading')} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${styles.contentRuleCenter} ${styles.errorState}`}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIconWrapper}>
            <RiAlertLine className={styles.errorIcon} />
          </div>
          <Typography
            variant='text-xl'
            weight='bold'
            className={styles.errorTitle}
          >
            {translate('rules_center_something_wrong')}
          </Typography>
          <Typography variant='text-md' className={styles.errorDescription}>
            {error || translate('rules_center_load_error')}
          </Typography>
          {onRetry && (
            <button
              type='button'
              onClick={onRetry}
              className={styles.retryButton}
            >
              <RiRestartLine className={styles.retryIcon} />
              <span className={styles.retryButtonText}>
                {translate('try_again')}
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state - only show when no categories exist and not loading
  if (categories.length === 0 && !isLoading && !isTableLoading) {
    return (
      <div className={styles.contentRuleCenter}>
        <div className={styles.emptyContainer}>
          <div className={styles.emptyIconWrapper}>
            <RiFileList3Line size={48} className={styles.emptyIcon} />
          </div>
          <Typography
            variant='text-xl'
            weight='bold'
            className={styles.emptyTitle}
          >
            {translate('rules_center_no_rules')}
          </Typography>
          <Typography variant='text-md' className={styles.emptyDescription}>
            {translate('rules_center_no_rules_description')}
          </Typography>
          {hasActiveFilters && (
            <Button
              type='button'
              variant='secondary'
              colorScheme='primary'
              onClick={handleResetFilters}
              className={styles.resetButton}
            >
              {translate('rules_center_reset_filters')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentRuleCenter}>
      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <QualityCard className={styles.statCard}>
          <div className={styles.statContent}>
            <div className={styles.statIconWrapper}>
              <RiFileList3Line size={20} className={styles.statIcon} />
            </div>
            <div className={styles.statDetails}>
              <div className={styles.statLabel}>
                {translate('rules_center_all_rules')}
              </div>
              <div className={styles.statValue}>{allRulesCount}</div>
            </div>
          </div>
        </QualityCard>

        <QualityCard className={styles.statCard}>
          <div className={styles.statContent}>
            <div
              className={`${styles.statIconWrapper} ${styles.statIconWrapperMust}`}
            >
              <RiProhibited2Line size={20} className={styles.statIconMust} />
            </div>
            <div className={styles.statDetails}>
              <div className={styles.statLabel}>
                {translate('rules_center_must_comply')}
              </div>
              <div className={styles.statValue}>{mustComplyCount}</div>
            </div>
          </div>
        </QualityCard>

        <QualityCard className={styles.statCard}>
          <div className={styles.statContent}>
            <div
              className={`${styles.statIconWrapper} ${styles.statIconWrapperShould}`}
            >
              <RiAlertLine size={20} className={styles.statIconShould} />
            </div>
            <div className={styles.statDetails}>
              <div className={styles.statLabel}>
                {translate('rules_center_should_comply')}
              </div>
              <div className={styles.statValue}>{shouldComplyCount}</div>
            </div>
          </div>
        </QualityCard>

        <QualityCard className={styles.statCard}>
          <div className={styles.statContent}>
            <div
              className={`${styles.statIconWrapper} ${styles.statIconWrapperGood}`}
            >
              <RiFileInfoLine size={20} className={styles.statIconGood} />
            </div>
            <div className={styles.statDetails}>
              <div className={styles.statLabel}>
                {translate('rules_center_good_to_have')}
              </div>
              <div className={styles.statValue}>{goodToHaveCount}</div>
            </div>
          </div>
        </QualityCard>
      </div>
      {/* Sticky Header Container - Contains Filters + Table Header */}
      <div className={styles.stickyHeaderContainer}>
        {/* Filters */}
        <div className={styles.filtersSection}>
          <div className={styles.filterRow}>
            <div className={styles.filterContainer}>
              {countries.length > 0 && (
                <CapsuleDropdown
                  name='country'
                  label='Country'
                  list={countries}
                  value={filters.countries}
                  handleSelection={handleFilterChange('countries')}
                  multiSelect
                  isSearchable
                  className={styles.filterDropdown}
                  enableSelectAll
                  searchInputPlaceholder={translate('search_for_a_country')}
                  iconBefore={<RiFilter3Fill size={20} color='#0000C9' />}
                />
              )}
              {brands.length > 0 && (
                <CapsuleDropdown
                  name='brand'
                  label='Brand'
                  list={brands}
                  value={filters.brands}
                  handleSelection={handleFilterChange('brands')}
                  multiSelect
                  isSearchable
                  className={styles.filterDropdown}
                  enableSelectAll
                  isGroupDropDown
                  groupingConfig={brandGroupingConfig}
                  groupTooltips={brandTooltipConfig}
                  searchInputPlaceholder={translate('search_for_a_brand')}
                  iconBefore={<RiFilter3Fill size={20} color='#0000C9' />}
                />
              )}
              {audiences.length > 0 && (
                <CapsuleDropdown
                  name='audience'
                  label='Audience'
                  list={audiences}
                  value={filters.audiences}
                  handleSelection={handleFilterChange('audiences')}
                  multiSelect
                  className={styles.filterDropdown}
                  enableSelectAll
                  iconBefore={<RiFilter3Fill size={20} color='#0000C9' />}
                />
              )}
              {channels.length > 0 && (
                <CapsuleDropdown
                  name='channel'
                  label='Channel'
                  list={channels}
                  value={filters.channels}
                  handleSelection={handleFilterChange('channels')}
                  multiSelect
                  isSearchable={channels.length > 5}
                  searchInputPlaceholder={translate('search_for_a_channel')}
                  className={styles.filterDropdown}
                  enableSelectAll
                  iconBefore={<RiFilter3Fill size={20} color='#0000C9' />}
                />
              )}
              {categoryFilters.length > 0 && (
                <CapsuleDropdown
                  name='categories'
                  label='Categories'
                  list={categoryFilters.map((c, idx) => ({
                    id: `category-${idx}`,
                    name: c,
                    label: c,
                  }))}
                  value={filters.categories}
                  handleSelection={handleFilterChange('categories')}
                  multiSelect
                  isSearchable={categoryFilters.length > 5}
                  searchInputPlaceholder={translate('search_for_a_category')}
                  className={styles.filterDropdown}
                  enableSelectAll
                  iconBefore={<RiFilter3Fill size={20} color='#0000C9' />}
                />
              )}
              <button
                className={styles.resetFiltersButton}
                onClick={handleResetFilters}
                type='button'
                disabled={!hasActiveFilters}
              >
                <RiDeleteBack2Line size={16} />
                {translate('rules_center_reset_filters')}
              </button>
            </div>
            <Typography variant='text-sm' className={styles.showingCount}>
              {translate('rules_center_showing')} {visibleRulesCount}
            </Typography>
          </div>
        </div>

        {/* Table Header */}
        <div className={styles.rulesHeader}>
          <div className={styles.rulesHeaderColumn}>
            {translate('rules_center_rules_header')}
          </div>
          <div className={styles.rulesHeaderColumn}>
            {translate('rules_center_tags_header')}
          </div>
          <div
            className={`${styles.rulesHeaderColumn} ${styles.sortableColumn}`}
            onClick={() => onSortChange?.('severity')}
            role='button'
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onSortChange?.('severity')}
          >
            {translate('rules_center_severity_header')}
            {/* {renderSortIcon('severity')} */}
          </div>
          <div
            className={`${styles.rulesHeaderColumn} ${styles.sortableColumn}`}
            onClick={() => onSortChange?.('lastUpdated')}
            role='button'
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onSortChange?.('lastUpdated')}
          >
            {translate('rules_center_last_updated_header')}
            {/* {renderSortIcon('lastUpdated')} */}
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div className={styles.rulesSection}>
        {/* Rules Content - with table loading state */}
        <div className={styles.rulesContent}>
          {isTableLoading ? (
            <div className={styles.tableLoadingContainer}>
              <CustomLoader
                size={24}
                text={translate('rules_center_loading')}
              />
            </div>
          ) : (
            categories.map(category => {
              const severityCounts = getCategorySeverityCounts(category);
              const CategoryIcon = getCategoryIcon(category.id);
              const isCategoryLoading = category.isLoading;
              const isCategoryFetched = category.isFetched;

              return (
                <Accordion
                  key={category.id}
                  title={
                    <div className={styles.categoryTitleWrapper}>
                      <span className={styles.categoryTitle}>
                        {category.name}
                      </span>
                      <div className={styles.categorySeverityBadges}>
                        {severityCounts.must > 0 && (
                          <div className={styles.severityCountBadge}>
                            <RiProhibited2Line
                              size={16}
                              className={styles.severityIconMust}
                            />
                            <span className={styles.severityCountText}>
                              {severityCounts.must}
                            </span>
                          </div>
                        )}
                        {severityCounts.should > 0 && (
                          <div className={styles.severityCountBadge}>
                            <RiAlertLine
                              size={16}
                              className={styles.severityIconShould}
                            />
                            <span className={styles.severityCountText}>
                              {severityCounts.should}
                            </span>
                          </div>
                        )}
                        {severityCounts.good > 0 && (
                          <div className={styles.severityCountBadge}>
                            <RiFileInfoLine
                              size={16}
                              className={styles.severityIconGood}
                            />
                            <span className={styles.severityCountText}>
                              {severityCounts.good}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  }
                  customIcon={
                    CategoryIcon && (
                      <div className={styles.categoryIconWrapper}>
                        {CategoryIcon}
                      </div>
                    )
                  }
                  wrapperClassName={styles.accordion}
                  headerClassName={styles.accordionHeader}
                  contentClassName={styles.accordionContent}
                  defaultOpen={false}
                  onOpen={() => onAccordionOpen?.(category.id)}
                >
                  <div ref={el => setScrollContainerRef(category.id, el)}>
                    {isCategoryLoading ? (
                      <div className={styles.accordionLoadingContainer}>
                        <CustomLoader
                          size={20}
                          text={translate('rules_center_loading')}
                        />
                      </div>
                    ) : !isCategoryFetched ? (
                      <div className={styles.accordionEmptyContainer}>
                        <Typography
                          variant='text-sm'
                          className={styles.emptyText}
                        >
                          {translate('rules_center_open_accordion')}
                        </Typography>
                      </div>
                    ) : category.rules.length === 0 ? (
                      <div className={styles.accordionEmptyContainer}>
                        <Typography
                          variant='text-sm'
                          className={styles.emptyText}
                        >
                          {translate('rules_center_no_rules_category')}
                        </Typography>
                      </div>
                    ) : (
                      <>
                        {category.rules.map(rule => (
                          <div key={rule.id} className={styles.ruleRow}>
                            <div className={styles.ruleCell}>
                              <Typography
                                variant='text-md'
                                weight='bold'
                                className={styles.ruleName}
                              >
                                {rule.name}
                              </Typography>
                              {renderDescription(rule)}
                            </div>
                            <div className={styles.ruleCell}>
                              {renderTags(rule)}
                            </div>
                            <div className={styles.ruleCell}>
                              <div
                                className={`${styles.severityBadge} ${
                                  rule.severity === SeverityCode.BLOCKER
                                    ? styles.severityBadgeMust
                                    : rule.severity === SeverityCode.WARNING
                                      ? styles.severityBadgeShould
                                      : styles.severityBadgeGood
                                }`}
                              >
                                {getSeverityIcon(rule.severity)}
                                <Typography
                                  variant='text-sm'
                                  weight='bold'
                                  className={styles.severityText}
                                >
                                  {getSeverityLabel(rule.severity)}
                                </Typography>
                              </div>
                            </div>
                            <div className={styles.ruleCell}>
                              <Typography
                                variant='text-sm'
                                className={styles.dateText}
                              >
                                {rule.lastUpdated}
                              </Typography>
                            </div>
                          </div>
                        ))}
                        {/* Loading indicator shown while loading more rules */}
                        {category.isLoading && category.rules.length > 0 && (
                          <div className={styles.loadMoreContainer}>
                            <CustomLoader
                              size={20}
                              text={translate('rules_center_loading_more')}
                            />
                          </div>
                        )}
                        {/* Infinite scroll sentinel - hidden element to trigger loading */}
                        {category.pagination &&
                          category.pagination.page <
                            category.pagination.totalPages &&
                          !category.isLoading && (
                            <div
                              ref={el => setupInfiniteScroll(category.id, el)}
                              style={{ height: '20px', visibility: 'hidden' }}
                            />
                          )}
                        {/* Load More button for manual pagination */}
                        {category.pagination &&
                          category.pagination.page <
                            category.pagination.totalPages &&
                          !category.isLoading && (
                            <div className={styles.loadMoreContainer}>
                              <Button
                                type='button'
                                variant='secondary'
                                colorScheme='primary'
                                onClick={() => onLoadMoreRules?.(category.id)}
                                className={styles.loadMoreButton}
                              >
                                {translate('rules_center_load_more')}
                              </Button>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </Accordion>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

ContentRuleCenter.displayName = 'ContentRuleCenter';

export default ContentRuleCenter;
